import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { Logger } from 'src/app/common/logging/logger';

import {
  CreateConnectionError,
  PadDevice,
  PadSearchResponse,
  SignoPadApiState,
  SignoPadDeviceState,
  SignoPadSignature,
} from './signopad.types';

/**
 * STPadServerLib*** variables are initialized in src/assets/js/STPadServerLib-*.js
 * Lib is included via angular.json: app.architect.build.scripts
 * */
declare var STPadServerLibCommons;
declare var STPadServerLibApi;
declare var STPadServerLibDefault;

/**
 * SignoPadService Usage:
 * - init(): Registers STPadServer API Event handlers (use e.g. in ngOnInit())
 * - OPTION 1 (observe simple Ready-For-Signature State):
 *   subscribe to observeSignatures(), observeReadyForSignature()
 * - OPTION 2 (observe API and Device states):
 *   subscribe to observeSignatures(), observeSignoPadApiState(), observeSignoPadDeviceState()
 * - readyCheck() -> result will be emitted by observeSignoPadApiState(),
 *   observeSignoPadDeviceState(), observeReadyForSignature()
 * - startSignature() and wait for observeSignatures() to emit a signature
 * - tearDown(): Unregister event handlers (use e.g. in ngOnDestroy())
 */
@Injectable({ providedIn: 'root' })
export class SignoPadService {
  /**
   * _signatures$ emits SignoPadSignatures:
   * _signatureCanceled$ emits if signature was canceled on SignoPad device
   * image: contains base64 encoded png of signature
   * cert: contains base64 encoded X.509 public key certificate for signing device
   */
  // TODO private
  public _signatures$: BehaviorSubject<SignoPadSignature>;
  private _signatureCanceled$: BehaviorSubject<boolean>;
  private _signoPadApiState$: BehaviorSubject<SignoPadApiState>;
  private _signoPadDeviceState$: BehaviorSubject<SignoPadDeviceState>;
  private _demoMode = false;
  private _searchResponse: PadSearchResponse;

  /**
   * _readyForSignature$ emits:
   * (true) if API and device are connected -> ready for startSignature
   * (false) if API is disconnected or no device connected
   */
  private _readyForSignature$: Observable<boolean>;

  constructor(private _logger: Logger) {}

  /**
   * Initialize this service.
   * Needs to be called before use of the service as it registers device callbacks.
   */
  public init() {
    this._registerHandlers();
    this._initSubjects();
  }

  /**
   * Can be used to simulate device presence, emits _readyForSignature = true
   * even if no device is connected, startSignature() emits a random
   * signature after 2s
   */
  public demoMode() {
    this._demoMode = true;
  }

  public async teardown() {
    await this._disconnectAllDevices();
    await this.disconnectApi();
    this._unregisterHandlers();
  }

  public async readyCheck() {
    if (this._demoMode === true) {
      this._signoPadDeviceState$.next(SignoPadDeviceState.DEVICE_CONNECTED);
      this._signoPadApiState$.next(SignoPadApiState.API_CONNECTED);
    } else {
      try {
        await this._createConnection();
        this._searchResponse = await this._searchForPads();
        if (this._searchResponse?.foundPads?.length > 0) {
          try {
            await this._connectToDevice(this._searchResponse.foundPads[0]);
          } catch (error) {
            this._logger.error('readyCheck', error);
            this._signoPadDeviceState$.next(SignoPadDeviceState.DEVICE_DISCONNECTED);
          }
        } else {
          this._signoPadDeviceState$.next(SignoPadDeviceState.DEVICE_DISCONNECTED);
        }
      } catch (error) {
        if (error?.errorMessage === CreateConnectionError.DEVICE_CONNECTION_ALREADY_CREATED) {
          this._signoPadDeviceState$.next(SignoPadDeviceState.DEVICE_CONNECTED);
        } else {
          this._signoPadApiState$.next(SignoPadApiState.API_DISCONNECTED);
        }
      }
    }
  }

  public observeSignatureCanceled(): Observable<boolean> {
    return this._signatureCanceled$.pipe(filter(canceled => canceled === true));
  }

  public observeSignatures(): Observable<SignoPadSignature> {
    return this._signatures$.pipe(filter(signature => signature != null));
  }

  public observeSignoPadApiState(): Observable<SignoPadApiState> {
    return this._signoPadApiState$.pipe(filter(state => state != null));
  }

  public observeSignoPadDeviceState(): Observable<SignoPadDeviceState> {
    return this._signoPadDeviceState$.pipe(filter(state => state != null));
  }

  public observeReadyForSignature(): Observable<boolean> {
    return this._readyForSignature$.pipe(filter(ready => ready != null));
  }

  /**
   * A function that is used to test signatures.
   * If triggered, the smallest possible png is emmited after ms milliseconds
   * @param [ms=1000] - number = 1000
   */
  public testConfirmSignature(ms: number = 1000) {
    setTimeout(() => {
      this._signatures$.next({
        image: this._randomBase64Image(),
        cert: 'TESTCERT',
      });
    }, ms);
  }

  private _randomBase64Image(): string {
    const imageData = new ImageData(200, 60);
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 60;
    imageData.data.set(imageData.data.map(() => Math.floor(Math.random() * (255 - 1))));
    canvas.getContext('2d').putImageData(imageData, 0, 0);
    return canvas.toDataURL();
  }

  public async stopSignature(): Promise<void> {
    try {
      await this._stopSignature();
    } catch (e) {
      // ignore
    }
  }

  public async cancelSignature(): Promise<void> {
    try {
      await this._cancelSignature();
    } catch (e) {
      // ignore
    }
  }

  public async _cancelSignature(): Promise<void> {
    await STPadServerLibDefault.cancelSignature();
  }

  public async startSignature(title: string, signatureName: string): Promise<void> {
    if (this._demoMode === true) {
      this.testConfirmSignature();
    } else {
      try {
        await this._stopSignature();
      } catch (e) {
        // ignore
      }
      await this._startSignature(title, signatureName);
    }
  }

  public async getPublicKeyFromDevice(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      STPadServerLibDefault.getSigningCert().then(response => resolve(response.signingCert), reject);
    });
  }

  public async disconnectApi(): Promise<void> {
    await this._destroyConnection();
    if (!this._signoPadApiState$) {
      // init() has not been called
      return;
    }
    this._signoPadApiState$.next(SignoPadApiState.API_DISCONNECTED);
  }

  public async searchForPads(): Promise<PadSearchResponse> {
    return this._searchForPads();
  }

  public async connectToApi(): Promise<void> {
    return this._createConnection();
  }

  private _initSubjects() {
    this._signatures$ = new BehaviorSubject(null);
    this._signatureCanceled$ = new BehaviorSubject(false);
    this._signoPadApiState$ = new BehaviorSubject(null);
    this._signoPadDeviceState$ = new BehaviorSubject(null);

    /**
     * _readyForSignature$ emits:
     * (true) if API and device are connected -> ready for startSignature
     * (false) if API is disconnected or no device connected
     */
    this._readyForSignature$ = combineLatest([this._signoPadApiState$, this._signoPadDeviceState$]).pipe(
      filter(value => {
        return value[0] !== null && value[1] !== null;
      }),
      map(combinedState => {
        return (
          combinedState[0] === SignoPadApiState.API_CONNECTED &&
          combinedState[1] === SignoPadDeviceState.DEVICE_CONNECTED
        );
      })
    );
  }

  private _registerHandlers() {
    STPadServerLibDefault.handleConfirmSignature = this._handleConfirmSignature.bind(this);
    STPadServerLibDefault.handleRetrySignature = this._handleRetrySignature.bind(this);
    STPadServerLibDefault.handleCancelSignature = this._handleCancelSignature.bind(this);
    STPadServerLibDefault.handleError = this._handleError.bind(this);
  }

  private _unregisterHandlers() {
    delete STPadServerLibDefault.handleConfirmSignature;
    delete STPadServerLibDefault.handleRetrySignature;
    delete STPadServerLibDefault.handleCancelSignature;
    delete STPadServerLibDefault.handleError;
  }

  private async _searchForPads(): Promise<PadSearchResponse> {
    return new Promise((resolve, reject) => {
      const params = new STPadServerLibDefault.Params.searchForPads();
      params.setPadSubset('HID');
      STPadServerLibDefault.searchForPads(params).then(resolve, reject);
    });
  }

  private async _startSignature(fieldName: string, customText: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const params = new STPadServerLibDefault.Params.startSignature();
      params.setFieldName(fieldName);
      params.setCustomText(customText);
      STPadServerLibDefault.startSignature(params).then(resolve, reject);
    });
  }

  private async _stopSignature(): Promise<void> {
    return new Promise((resolve, reject) => {
      STPadServerLibDefault.stopSignature().then(resolve, reject);
    });
  }

  private async _connectToDevice(padDevice: PadDevice): Promise<void> {
    return new Promise((resolve, reject) => {
      const params = new STPadServerLibDefault.Params.openPad(padDevice.index);
      STPadServerLibDefault.openPad(params).then(
        connection => {
          this._signoPadDeviceState$.next(SignoPadDeviceState.DEVICE_CONNECTED);
          resolve(connection);
        },
        error => {
          this._logger.error('_connectToDevice', error);
          this._signoPadDeviceState$.next(SignoPadDeviceState.DEVICE_DISCONNECTED);
          reject(error);
        }
      );
    });
  }

  private async _closePad(padDevice: PadDevice): Promise<void> {
    return new Promise((resolve, error) => {
      const params = new STPadServerLibDefault.Params.closePad(padDevice.index);
      STPadServerLibDefault.closePad(params).then(resolve, error);
    });
  }

  private async _destroyConnection(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        STPadServerLibCommons.destroyConnection();
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  private async _createConnection(websocket?: string) {
    const self = this;
    return new Promise<void>((resolve, reject) => {
      try {
        return STPadServerLibCommons.createConnection(
          websocket || 'wss://local.signotecwebsocket.de:49494',
          () => {
            resolve();
            this._onApiOpen.bind(self)();
          },
          this._onApiClose.bind(self),
          error => {
            this._logger.error('_createConnection', error);
            reject();
            this._onError.bind(self)(error);
          }
        );
      } catch (error) {
        if (error === CreateConnectionError.WS_ALREADY_CREATED) {
          // ignore, WS already opened
          resolve();
          this._onApiOpen.bind(self)();
        }
      }
    });
  }

  _handleError(error: { errorContext: string; errorCorde: number; errorDescription: string }) {
    this._logger.error('_handleError', new Error(JSON.stringify(error)));
    this._signoPadApiState$.error(error);
  }
  _handleCancelSignature() {
    this._logger.info('_handleCancelSignature');
    this._signatureCanceled$.next(true);
    STPadServerLibDefault.cancelSignature();
  }
  _handleRetrySignature() {
    this._logger.info('_handleRetrySignature');
    STPadServerLibDefault.retrySignature();
  }

  async _handleConfirmSignature() {
    this._logger.info('_handleConfirmSignature');
    await STPadServerLibDefault.confirmSignature();
    try {
      const response = await this._getSignatureImage();
      const pubCert = await this.getPublicKeyFromDevice();
      this._signatures$.next({ image: 'data:image/png;base64,' + (response as any).file, cert: pubCert });
    } catch (error) {
      this._logger.error('_handleConfirmSignature', error);
      this._signatures$.error(error);
    }
  }

  private async _disconnectAllDevices() {
    if (this._searchResponse != null && this._searchResponse.foundPads != null) {
      this._searchResponse.foundPads.map(async pad => this._closePad(pad));
    }
  }

  private _getSignatureImage() {
    return new Promise((resolve, reject) => {
      const getSignatureDataParams = new STPadServerLibDefault.Params.getSignatureImage();
      STPadServerLibDefault.getSignatureImage(getSignatureDataParams).then(resolve, reject);
    });
  }

  _onApiOpen() {
    this._signoPadApiState$.next(SignoPadApiState.API_CONNECTED);
  }
  _onApiClose() {
    this._signoPadApiState$.next(SignoPadApiState.API_DISCONNECTED);
  }
  _onError(error: any) {
    this._signoPadApiState$.error(error);
  }
}
